
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { TextField } from '@mui/material';
import { Button } from '__common/components/Button';
import SVG from 'react-inlinesvg';
import { REMOVE_OBSTRUCTION, CHANGE_OBSTRUCTION_NAME, CHANGE_OBSTRUCTION_HEIGHT, SET_EDITING_OBSTRUCTION_ID } from './ObstructionsActions';
import { OPEN_DRAWER_PAGE } from 'projectDesign/components/projectConfiguration/projectConfigurationActions';
import { RECHECK_PANELS_OBSTRUCTION_ZONE } from '../panels/panelsActions';
import { getObstructionLargestEdgeMinimumLength, getObstructionLargestEdgeMinimumLengthForNewProjects } from './obstructions';
import { obstructionCheckFormulaModification } from '__common/utils/versionCompare/versionCompare';
import { state } from '__common/store';

type Props = {
  hideDrawer: () => void,
  deleteObstruction: (roofId: number, obstructionId: number, background?: any, productId?: number) => void,
  changeObstructionName: (roofId: number, obstructionId: number, name: string) => void,
  changeObstructionHeight: (roofId: number, obstructionId: number, height: string, background?: any, productId?: number) =>  void,
  setEditingObstructionId: (obstructionId: number) => void,
  roofsObstructions: roofsObstructions,
  roofId: number,
  background?: any,
  productId?: number,
};

class ObstructionsComponent extends React.Component<Props> {
  componentDidUpdate() {
    const roofObstructions = this.getRoofObstructions();
    if (!roofObstructions || roofObstructions.length === 0) {
      this.props.hideDrawer();
    }
  }

  componentWillUnmount() {
    this.props.setEditingObstructionId(null);
  }
  
  removeObstruction = (obstructionId: number) => {
    const { roofId, background, productId } = this.props;

    this.props.deleteObstruction(roofId, obstructionId, background, productId);
  }

  obstructionWarning = (obstruction: { name: string, id: number, height: number, isHighEnough: boolean }) => {
    if (obstruction.isHighEnough) {
      return null;
    }

    return (
      <div className="input-warning">
        This obstruction does not affect wind pressure
      </div>
    );
  }

  getRoofObstructions = (): { name: string, id: number, height: number, length: number, width: number, isHighEnough: boolean }[] => {
    const obstructionFields = [];
    const { roofsObstructions, roofId } = this.props;
    const { projectConfiguration: { projectVersion } } = state();
    if (roofsObstructions[roofId] && Object.keys(roofsObstructions[roofId]).length) {
      const obstructions = roofsObstructions[roofId];
      Object.keys(obstructions).map((obstructionId) => {
        const obstruction: obstruction = obstructions[obstructionId];
        const minimumHeightInches = obstructionCheckFormulaModification(projectVersion) ? getObstructionLargestEdgeMinimumLengthForNewProjects(5) : getObstructionLargestEdgeMinimumLength(5);
        const isHighEnough = obstruction.height > minimumHeightInches && obstruction.isLargeEnough;
        
        obstructionFields.push({
          name: obstruction.name,
          id: Number(obstructionId),
          height: obstruction.height,
          length: obstruction.length,
          width: obstruction.width,
          isHighEnough,
        });
      });
    }

    return obstructionFields;
  }

  render() {
    const { roofId } = this.props;
    const roofObstructions = this.getRoofObstructions();

    if (roofObstructions.length) {
      const obstructions = roofObstructions.map((obstruction) => (
        <Fragment key={obstruction.id}>
        {this.obstructionWarning(obstruction)}
        <div className="obstruction">
          <div className="name">
          <TextField
            className={`obstruction-input`}
            label="Name"
            type="text"
            onFocus={() => this.props.setEditingObstructionId(obstruction.id)}
            onBlur={() => this.props.setEditingObstructionId(null)}
            placeholder="Set obstruction name"
            value={obstruction.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.props.changeObstructionName(roofId, obstruction.id, event.target.value)}
            fullWidth
            variant="standard"
            InputLabelProps={{ shrink: true }}
          />
          </div>
          <div className="height">
            <TextField
              className={!obstruction.height || obstruction.height === 0 ? `obstruction-warning obstruction-input` : `obstruction-input`}
              id="floating-center-title"
              label="Height (IN)"
              type="number"
              onFocus={() => this.props.setEditingObstructionId(obstruction.id)}
              onBlur={() => this.props.setEditingObstructionId(null)}
              placeholder="Set obstruction height"
              defaultValue={obstruction.height}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const { background, productId } = this.props;
                this.props.changeObstructionHeight(roofId, obstruction.id, event.target.value, background, productId);
              }}
              fullWidth
              variant="standard"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="delete">
            <Button 
              onMouseEnter={() => this.props.setEditingObstructionId(obstruction.id)} 
              onMouseLeave={() => this.props.setEditingObstructionId(null)}
              onClick={() => this.removeObstruction(obstruction.id)} 
              className="delete-button" 
              tooltipLabel="Remove"
            >
              <SVG src={require('assets/media/icons/plus_icon.svg')} />
            </Button>
          </div>
        </div>
        </Fragment>
      ));

      return (
        <div className="obstructions">
          <div className="drawer-section-title">Roof Obstructions</div>
          {obstructions}
        </div>
      );
    }

    return null;
  }
}

const mapStateToProps = (appState: appState) => ({
  obstructions: appState.obstructions, // to refresh component only
  roofsObstructions: appState.obstructions.obstructions,
  roofId: appState.background.selectedRoofId,
  background: appState.background,
  productId: appState.projectConfiguration.productId,
});

const mapDispatchToProps = (dispatch) => ({
  changeObstructionName: (roofId: number, obstructionId: number, name: string) => {
    dispatch(SET_EDITING_OBSTRUCTION_ID(obstructionId));
    dispatch(CHANGE_OBSTRUCTION_NAME(roofId, obstructionId, name));
  },
  changeObstructionHeight: (roofId: number, obstructionId: number, height: string, background?: any, productId?: number) => {
    dispatch(SET_EDITING_OBSTRUCTION_ID(obstructionId));
    dispatch(CHANGE_OBSTRUCTION_HEIGHT(roofId, obstructionId, height));
    if (background) {
      const { selectedRoofId, metersPerPixel, bgXY: { x, y } } = background;
      dispatch(RECHECK_PANELS_OBSTRUCTION_ZONE(selectedRoofId, metersPerPixel, x, y, productId));
    } else {
      dispatch(RECHECK_PANELS_OBSTRUCTION_ZONE());
    }
  },
  deleteObstruction: (roofId: number, obstructionId: number, background?: any, productId?: number) => {
    dispatch(REMOVE_OBSTRUCTION(roofId, obstructionId));
    if (background) {
      const { selectedRoofId, metersPerPixel, bgXY: { x, y } } = background;
      dispatch(RECHECK_PANELS_OBSTRUCTION_ZONE(selectedRoofId, metersPerPixel, x, y, productId));
    } else {
      dispatch(RECHECK_PANELS_OBSTRUCTION_ZONE());
    }
  },
  setEditingObstructionId: (obstructionId: number) => dispatch(SET_EDITING_OBSTRUCTION_ID(obstructionId)),
  hideDrawer: () => dispatch(OPEN_DRAWER_PAGE(null)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ObstructionsComponent);
